<template>
    <VfModal ref="modal" class="m-talk-subscriptions zs-mn-modal" scrolls @click.stop>
        <template #header>
            <div class="zs-mn-modal__hdr">
                <div class="zs-mn-modal__title">My Numbers &amp; Notifications</div>
                <div class="zs-mn-modal__sub">Choose which numbers ring on which devices.</div>
            </div>
        </template>

        <table class="zs-table zs-mn-tbl">
            <thead>
                <tr>
                    <th>Number / Extension</th>
                    <th v-tooltip="MY_NUMBERS_TIP" class="zs-mn-tbl__th-icon">
                        <i class="fa-solid fa-star" />
                    </th>
                    <th v-tooltip="'Desktop Browser Notifications'" class="zs-mn-tbl__th-icon">
                        <i class="fa-regular fa-desktop" />
                    </th>
                    <th v-tooltip="'Mobile Notifications'" class="zs-mn-tbl__th-icon">
                        <i class="fa-regular fa-mobile-vibrate" />
                    </th>
                    <th v-if="hasSipRows" class="zs-mn-tbl__th-calls">
                        <span>Mobile Calls</span>
                        <i
                            v-tooltip="
                                'All Calls will forward all incoming calls for the subscribed extension to your ZynoTalk mobile app, including those arriving through a ring group.\n\nDirect Calls will only forward those made or transferred directly to the subscribed extension.'
                            "
                            class="fa-regular fa-circle-question zs-mn-tbl__help"
                        />
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="row in rows" :key="row.objectId">
                    <td>{{ row.label }}</td>
                    <td>
                        <button
                            type="button"
                            class="zs-mn-tog zs-mn-tog--star"
                            :class="{ 'is-on': row.primary || row.starred, 'is-disabled': row.primary }"
                            :disabled="row.primary"
                            @click="row.starred = !row.starred"
                        >
                            <i :class="row.primary || row.starred ? 'fa-solid fa-star' : 'fa-regular fa-star'" />
                        </button>
                    </td>
                    <td>
                        <button type="button" class="zs-mn-tog" :class="{ 'is-on': row.browserPush }" @click="row.browserPush = !row.browserPush">
                            <i v-if="row.browserPush" class="fa-solid fa-check" />
                        </button>
                    </td>
                    <td>
                        <button type="button" class="zs-mn-tog" :class="{ 'is-on': row.mobilePush }" @click="row.mobilePush = !row.mobilePush">
                            <i v-if="row.mobilePush" class="fa-solid fa-check" />
                        </button>
                    </td>
                    <td v-if="hasSipRows">
                        <select v-if="row.enableSip" v-model="row.mobileCalls" class="zs-mn-sel" :disabled="!row.mobilePush">
                            <option :value="null">No Calls</option>
                            <option value="all">All Calls</option>
                            <option value="direct">Direct Calls</option>
                        </select>
                    </td>
                </tr>
            </tbody>
        </table>

        <template #footer>
            <button
                v-tooltip="'What do these settings do?'"
                type="button"
                class="zs-btn zs-btn--icon left"
                aria-label="What do these settings do?"
                @click="showHelp"
            >
                <i class="fa-regular fa-circle-question" />
            </button>
            <button type="button" class="zs-btn" @click="callback()">Cancel</button>
            <button type="button" class="zs-btn zs-btn--primary" @click="save">Save</button>
        </template>
    </VfModal>
</template>

<script lang="ts" setup>
import { handleErrorAndAlert, presentOverlay, VfModal, vfModalRef } from '@zyno-io/vue-foundation';
import { keyBy } from 'lodash';
import { computed, onMounted, ref } from 'vue';

import type { IExtensionResponse, INumberResponse, ISubscribedNumber } from '@/openapi-clients-generated/talk';

import { GlobalState } from '@/global';
import { TalkSubscribedNumbersApi } from '@/openapi-clients-generated/talk';
import { DataCache } from '@/shared/data/cache';
import { BrowserPush } from '@/shared/services/browser-push';

import { useTalkHelpers } from '../store';
import MSubscriptionsHelp from './m-subscriptions-help.vue';

const modal = vfModalRef();

const props = defineProps<{
    callback: (success?: boolean) => void;
}>();

const { formatNumber } = useTalkHelpers();

const MY_NUMBERS_TIP =
    "Add to My Numbers, the number group Talk shows by default. Starred extensions also show live calls in the phone dock and are listed first under Call From. Doesn't change notifications.";

const numbersRef = DataCache.getRef('talkNumbers');
const extensionsRef = DataCache.getRef('talkExtensions');
const subscribedRef = DataCache.getRef('talkSubscribedNumbers');
const accessRef = DataCache.getRef('talkAccess');

const numbers = computed(() => numbersRef.value);
const extensions = computed(() => extensionsRef.value);
const subscribed = computed(() => subscribedRef.value ?? []);

type Row = {
    objectType: 'number' | 'extension';
    objectId: string;
    data: INumberResponse | IExtensionResponse;
    label: string;
    primary?: boolean;
    starred?: boolean;
    browserPush?: boolean;
    mobilePush?: boolean;
    mobileCalls?: 'all' | 'direct' | null;
    enableSip?: boolean;
};

const rows = ref<Row[]>([]);
const hasSipRows = computed(() => rows.value.some(r => r.enableSip));
onMounted(async () => {
    // The phone dialer can open this outside Talk, before every list below has loaded.
    await Promise.all([numbersRef, extensionsRef, subscribedRef, accessRef].map(r => r.loadPromise));
    const failed = [numbersRef, extensionsRef, subscribedRef].find(r => !r.value);
    if (failed) {
        handleErrorAndAlert(failed.loadError ?? 'Your numbers could not be loaded.');
        props.callback();
        return;
    }

    const currentSubs = keyBy(subscribed.value, s => s.objectId);
    rows.value = [
        ...numbers
            .value!.filter(n => !n.deletedAt)
            .filter(
                n =>
                    GlobalState.tenant.value?.isAdmin ||
                    accessRef.value?.find(a => a.objectType === 'number' && (a.objectId === n.id || a.objectId === null))
            )
            .map(n => ({ ...currentSubs[n.id], objectType: 'number' as const, objectId: n.id, data: n, label: formatNumber(n) })),
        ...extensions
            .value!.filter(e => !e.deletedAt)
            .filter(
                e =>
                    GlobalState.tenant.value?.isAdmin ||
                    accessRef.value?.find(a => a.objectType === 'extension' && (a.objectId === e.id || a.objectId === null))
            )
            .map(e => ({
                ...currentSubs[e.id],
                objectType: 'extension' as const,
                objectId: e.id,
                data: e,
                label: formatNumber(e),
                enableSip: e.enableSip
            }))
    ].sort((a, b) => a.label.localeCompare(b.label));
});

function showHelp() {
    presentOverlay(MSubscriptionsHelp, {});
}

async function save() {
    modal.value?.mask();
    try {
        // Start from the Save click so browsers can show the permission prompt before any API awaits.
        const browserRegistration = rows.value.some(row => row.browserPush) ? BrowserPush.registerNotifications() : undefined;
        const current = new Map<string, ISubscribedNumber>();
        for (const s of subscribed.value) current.set(s.objectId, s);

        for (const r of rows.value) {
            const key = r.objectId;
            const prev = current.get(key);

            if (!r.primary && !r.starred && !r.browserPush && !r.mobilePush) {
                if (prev) {
                    await TalkSubscribedNumbersApi.deleteSubscribedNumbersDelete({
                        body: {
                            objectType: r.objectType,
                            objectId: r.objectId
                        }
                    });
                }
            } else if (
                r.starred !== prev?.starred ||
                r.browserPush !== prev?.browserPush ||
                r.mobilePush !== prev?.mobilePush ||
                r.mobileCalls !== prev?.mobileCalls
            ) {
                await TalkSubscribedNumbersApi.postSubscribedNumbersStore({
                    body: {
                        objectType: r.objectType,
                        objectId: r.objectId,
                        starred: r.starred ?? false,
                        browserPush: r.browserPush ?? false,
                        mobilePush: r.mobilePush ?? false,
                        mobileCalls: r.mobileCalls ?? null
                    }
                });
            }
        }

        if (browserRegistration) await browserRegistration;

        await DataCache.getRef('talkSubscribedNumbers').backgroundReload?.();
        props.callback(true);
    } catch (err) {
        handleErrorAndAlert(err);
        modal.value?.unmask();
    }
}
</script>

<style scoped>
@reference '#tw.css';

:deep(.vf-modal-content) {
    @apply p-0;
}
</style>
